import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";

import {Alert, Button, Card, Empty, Flex, message, Spin, Table, Tag, Tooltip, Typography} from "antd";
import {
    ClockCircleOutlined,
    DownloadOutlined,
    ReloadOutlined,
    SyncOutlined,
    ThunderboltOutlined,
    WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import Apiurl from "../ConstURL";

dayjs.extend(utc);

const {Text, Title} = Typography;

// Даты отчётов хранятся как полночь UTC — берём дату из строки, без сдвига часового пояса.
const fmtDay = (iso) => (iso && !iso.startsWith("0001") ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : "—");
const fmtMsk = (iso) => (iso && !iso.startsWith("0001") ? dayjs(iso).utcOffset(180).format("DD.MM.YYYY HH:mm") + " МСК" : "");

const cardStyle = {width: "100%", maxWidth: 1100, borderRadius: 16, border: "1px solid #2a2140"};

export default function ListingsCheck() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadSeq = useRef(0);
    const [loadError, setLoadError] = useState(false);
    const [generating, setGenerating] = useState(""); // "" | "latest" | id дня

    const load = useCallback(() => {
        // Ответы могут прийти не по порядку — применяем только последний запрос.
        const seq = ++loadSeq.current;
        const token = localStorage.getItem("token");
        setLoading(true);
        axios.get(Apiurl + "/listings/overview?" + new URLSearchParams({token}))
            .then((res) => {
                if (seq !== loadSeq.current) return;
                setData(res.data);
                setLoadError(false);
            })
            .catch((err) => {
                if (seq !== loadSeq.current) return;
                setLoadError(true);
                if (err?.response?.status === 404) {
                    // 404 бывает и при истёкшей сессии, и если сервер ещё без этого раздела — проверяем сессию.
                    axios.get(Apiurl + "/user/auth/check?" + new URLSearchParams({token})).catch((e) => {
                        if (e?.response?.status === 404) {
                            localStorage.setItem("token", "");
                            window.location.replace("/login");
                        }
                    });
                }
            })
            .finally(() => {
                if (seq === loadSeq.current) setLoading(false);
            });
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const generate = (day) => {
        setGenerating(day || "latest");
        const params = {token: localStorage.getItem("token")};
        if (day) params.date = day;
        axios.post(Apiurl + "/listings/diff/generate?" + new URLSearchParams(params))
            .then((res) => {
                if (res.data.Status) {
                    message.success(res.data.Message, 6);
                    load();
                } else {
                    message.warning(res.data.Message, 8);
                }
            })
            .catch((err) => message.error(err?.response?.data?.Message || "Не удалось сформировать сравнение", 10))
            .finally(() => setGenerating(""));
    };

    const shopName = useMemo(() => {
        const m = {};
        (data?.Shops || []).forEach((s) => (m[s.Id] = s.Name));
        return (id) => m[id] || id;
    }, [data]);

    // Магазины, которые загружали листинги хотя бы в один из показанных дней: для них
    // отсутствие отчёта за день — повод проверить загрузку.
    const uploadDays = useMemo(() => data?.Uploads || [], [data]);
    const knownShops = useMemo(() => {
        const ids = new Set();
        uploadDays.forEach((d) => (d.Shops || []).forEach((s) => ids.add(s.ShopId)));
        return [...ids].sort((a, b) => shopName(a).localeCompare(shopName(b)));
    }, [uploadDays, shopName]);

    const downloadHref = (r) =>
        Apiurl + "/listings/diff/file?" + new URLSearchParams({token: localStorage.getItem("token"), id: r.Id, v: r.CreatedAt});

    const columns = [
        {
            title: "Файл",
            dataIndex: "Filename",
            render: (v, r) => (
                <Flex vertical gap="4px">
                    <Text strong>{v}</Text>
                    <Text type="secondary" style={{fontSize: 12}}>
                        {fmtMsk(r.CreatedAt)} · {r.Trigger === "manual" ? "вручную" : "автоматически"}
                    </Text>
                </Flex>
            ),
        },
        {
            title: "Сравнение",
            render: (_, r) => (
                <span style={{whiteSpace: "nowrap"}}>
                    {fmtDay(r.PrevDate)} → <b>{fmtDay(r.CurrDate)}</b>
                </span>
            ),
        },
        {
            title: "Пропало ASIN",
            dataIndex: "MissingAsins",
            align: "right",
            render: (v, r) => (
                <Tooltip title={`Строк в файле: ${r.MissingRows}. ASIN было: ${r.PrevAsins}, стало: ${r.CurrAsins}.`}>
                    <Text strong style={{color: v > 0 ? "#ff7875" : undefined}}>{v}</Text>
                </Tooltip>
            ),
        },
        {
            title: "Магазины",
            render: (_, r) => (
                <Flex gap="4px" wrap="wrap">
                    <Tooltip title={(r.CheckedShops || []).join(", ")}>
                        <Tag color="green">проверено: {(r.CheckedShops || []).length}</Tag>
                    </Tooltip>
                    {(r.UncheckedShops || []).length > 0 && (
                        <Tooltip title={`Нет отчёта за ${fmtDay(r.CurrDate)}: ${r.UncheckedShops.join(", ")}`}>
                            <Tag color="red">без отчёта: {r.UncheckedShops.length}</Tag>
                        </Tooltip>
                    )}
                </Flex>
            ),
        },
        {
            title: "Предупреждения",
            dataIndex: "Warnings",
            render: (w) =>
                w && w.length > 0 ? (
                    <Tooltip
                        overlayStyle={{maxWidth: 560}}
                        title={w.map((x, i) => <div key={i} style={{marginBottom: 6}}>• {x}</div>)}
                    >
                        <Tag color="orange" icon={<WarningOutlined/>}>{w.length}</Tag>
                    </Tooltip>
                ) : (
                    <Text type="secondary">нет</Text>
                ),
        },
        {
            title: "",
            align: "right",
            render: (_, r) => (
                <Flex gap="6px" justify="flex-end">
                    <Tooltip title={data?.Regenerable?.[r.Id]
                        ? "Пересчитать сравнение за этот день по текущим отчётам"
                        : "Пересчитать нельзя: отчёты этого сравнения уже удалены по сроку хранения"}>
                        <Button icon={<SyncOutlined/>} loading={generating === r.Id}
                                disabled={!data?.Regenerable?.[r.Id] || (generating !== "" && generating !== r.Id)}
                                onClick={() => generate(r.Id)}/>
                    </Tooltip>
                    <Button icon={<DownloadOutlined/>} href={downloadHref(r)} target="_blank" rel="noreferrer">
                        Excel
                    </Button>
                </Flex>
            ),
        },
    ];

    const sched = data?.Scheduler;
    const latest = data?.Diffs?.[0];

    if (loadError && !data) {
        return (
            <div style={{padding: "24px 28px"}}>
                <Flex vertical align="center">
                    <Alert
                        type="error"
                        showIcon
                        style={cardStyle}
                        message="Не удалось загрузить данные проверки листингов"
                        action={<Button onClick={load} loading={loading}>Повторить</Button>}
                    />
                </Flex>
            </div>
        );
    }

    return (
        <div style={{padding: "24px 28px"}}>
            <Flex vertical align="center" gap="24px">
                <Card style={cardStyle} styles={{body: {padding: 28}}}>
                    <Flex justify="space-between" align="flex-start" gap="16px" wrap="wrap">
                        <Flex vertical gap="6px" style={{maxWidth: 700}}>
                            <Title level={4} style={{margin: 0}}>Проверка листингов</Title>
                            <Text type="secondary">
                                Для каждого дня отчётов All Listings программа находит товары (ASIN), которые были в
                                отчётах предыдущего дня хотя бы в одном магазине, но пропали из всех отчётов этого дня.
                                В Excel — SKU, ASIN, количество, статус и магазин из отчёта предыдущего дня.
                            </Text>
                            <Text type="secondary">
                                Отчёты, загруженные за день, сравниваются в 05:00 МСК следующего рабочего дня
                                (пятничные — в понедельник). Если отчёт перезалили или магазин загрузили позже,
                                сравнение за этот день пересчитается при следующей проверке. Магазин без отчёта за день
                                в проверке этого дня не участвует. «Сформировать сейчас» сравнивает последний
                                загруженный день сразу.
                            </Text>
                            <Flex gap="8px" wrap="wrap" style={{marginTop: 6}}>
                                <Tag icon={<ClockCircleOutlined/>} color="purple">
                                    Автоматически: {sched?.Schedule || "пн–пт в 05:00 МСК"}
                                </Tag>
                                {sched && sched.Enabled === false && (
                                    <Tag color="red">автозапуск на этом сервере выключен</Tag>
                                )}
                                {fmtMsk(sched?.LastAutoAt) && <Tag>Последний автозапуск: {fmtMsk(sched.LastAutoAt)}</Tag>}
                            </Flex>
                            {sched?.LastResult && (
                                <Text type="secondary" style={{fontSize: 12}}>{sched.LastResult}</Text>
                            )}
                        </Flex>
                        <Flex gap="8px" wrap="wrap">
                            <Button icon={<ReloadOutlined/>} onClick={load} loading={loading}>Обновить</Button>
                            <Button type="primary" icon={<ThunderboltOutlined/>} onClick={() => generate("")}
                                    loading={generating === "latest"} disabled={generating !== "" && generating !== "latest"}>
                                Сформировать сейчас
                            </Button>
                        </Flex>
                    </Flex>

                    {latest && latest.Warnings && latest.Warnings.length > 0 && (
                        <Alert
                            type="warning"
                            showIcon
                            style={{marginTop: 18}}
                            message={`В последнем сравнении (${latest.Filename}) есть предупреждения`}
                            description={latest.Warnings.map((w, i) => <div key={i}>• {w}</div>)}
                        />
                    )}
                </Card>

                <Card style={cardStyle} styles={{body: {padding: 24}}} title="Загрузки отчётов All Listings по дням">
                    {loading && !data ? (
                        <Flex justify="center" style={{padding: 24}}><Spin/></Flex>
                    ) : uploadDays.length === 0 ? (
                        <Empty description="Отчёты All Listings ещё не загружались (раздел «Импорт данных» → «Листинги»)"/>
                    ) : (
                        <Flex vertical gap="14px">
                            {uploadDays.map((d) => {
                                const have = new Set((d.Shops || []).map((s) => s.ShopId));
                                const missing = knownShops.filter((id) => !have.has(id));
                                return (
                                    <Flex key={d.Date} gap="12px" align="flex-start" wrap="wrap">
                                        <div style={{minWidth: 150}}>
                                            <Text strong>{fmtDay(d.Date)}</Text>
                                            <div>
                                                <Text type="secondary" style={{fontSize: 12}}>
                                                    {have.size} из {knownShops.length} магазинов
                                                </Text>
                                            </div>
                                        </div>
                                        <Flex gap="6px" wrap="wrap" style={{flex: 1}}>
                                            {(d.Shops || [])
                                                .slice()
                                                .sort((a, b) => shopName(a.ShopId).localeCompare(shopName(b.ShopId)))
                                                .map((s) => (
                                                    <Tooltip key={s.ShopId}
                                                             title={`${s.Filenameold} · ${s.Rows} строк · загружен ${fmtMsk(s.TimeCreated)}`}>
                                                        <Tag color="green">{shopName(s.ShopId)}</Tag>
                                                    </Tooltip>
                                                ))}
                                            {missing.map((id) => (
                                                <Tooltip key={id} title="Отчёта за этот день нет">
                                                    <Tag color="red" style={{opacity: 0.8}}>{shopName(id)} — нет</Tag>
                                                </Tooltip>
                                            ))}
                                        </Flex>
                                    </Flex>
                                );
                            })}
                        </Flex>
                    )}
                </Card>

                <Card style={cardStyle} styles={{body: {padding: 24}}} title="Сравнения (Allist_Difference)">
                    {loadError && <Alert type="error" showIcon style={{marginBottom: 12}} message="Не удалось обновить данные — показаны последние загруженные"/>}
                    <Table
                        rowKey="Id"
                        size="middle"
                        loading={loading}
                        columns={columns}
                        dataSource={data?.Diffs || []}
                        pagination={{pageSize: 15, hideOnSinglePage: true}}
                        locale={{emptyText: <Empty description="Пока нет сформированных сравнений"/>}}
                        scroll={{x: true}}
                    />
                </Card>
            </Flex>
        </div>
    );
}
